import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  AiConfigKey,
  WorkflowSettings,
  WorkflowTemplate,
  WorkflowTemplateField,
  WorkflowTemplateVariable,
  WorkflowVariableSource,
} from '../../types';
import {
  AI_CONFIG_KEYS,
  AI_CONFIG_LABELS,
} from '../CustomBlocks/aiGenerator/aiConfig';
import { ADMIN_COLORS } from '../../theme';

interface WorkflowSettingsTabProps {
  settings?: WorkflowSettings | null;
  onChange: (settings: WorkflowSettings) => void;
  templates: WorkflowTemplate[] | null;
  loading: boolean;
  error: boolean;
  onReload: () => void;
  /** Names the form (or its custom fields) already provide; used for the ✓ markers. */
  presentNames: Set<string>;
  /** `field_name`s of the form's AI Generator fields, for `ai` variable sources. */
  aiFieldNames: string[];
  /** Forms that submit every step cannot create workflows (see backend). */
  submitEveryStep: boolean;
}

/** Form field whose answer names the created workflow (optional). */
const WORKFLOW_NAME_FIELD: WorkflowTemplateField = {
  name: 'workflowName',
  label: 'Workflow name',
  type: 'text',
  required: false,
  description: "When empty, the template's default name is used.",
};

const EMPTY: WorkflowSettings = { enabled: false, templateId: '' };

/** "Template default" — no entry, so the template's own value runs. */
const SOURCE_DEFAULT = '';

const sourceLabel = (source: WorkflowVariableSource | undefined): string => {
  if (!source) return 'the template default';
  if (source.type === 'value') return `"${source.value}"`;
  if (source.type === 'field') return `the answer to "${source.field}"`;
  return `the ${AI_CONFIG_LABELS[source.key]} setting of "${source.field}"`;
};

const fieldNote = (field: WorkflowTemplateField): string =>
  [
    field.description,
    field.crmSource ? `Use a CRM Select field (${field.crmSource}).` : '',
    field.type === 'select' && field.options ? `Accepted values: ${field.options.join(', ')}.` : '',
    field.default !== undefined ? `Default when empty: ${field.default}.` : '',
  ]
    .filter(Boolean)
    .join(' ');

/**
 * Workflow creation on submit. Only the template is chosen here; its values
 * come from form fields (or custom fields) with exactly the template's field
 * names. Changes apply to the builder immediately and are saved with the form.
 */
export const WorkflowSettingsTab: React.FC<WorkflowSettingsTabProps> = ({
  settings,
  onChange,
  templates,
  loading,
  error,
  onReload,
  presentNames,
  aiFieldNames,
  submitEveryStep,
}) => {
  const current = settings || EMPTY;
  const template = templates?.find((t) => t.id === current.templateId);
  const variables = template?.variables || [];

  /** Applies one variable's source (undefined = back to the template default). */
  const setVariableSource = (name: string, source?: WorkflowVariableSource) => {
    const variableSources = { ...(current.variableSources || {}) };
    if (source) variableSources[name] = source;
    else delete variableSources[name];

    const next: WorkflowSettings = { ...current };
    if (Object.keys(variableSources).length > 0) next.variableSources = variableSources;
    else delete next.variableSources;
    onChange(next);
  };

  const setVariableSourceType = (name: string, type: string) => {
    if (type === SOURCE_DEFAULT) {
      setVariableSource(name);
      return;
    }
    if (type === 'value') {
      setVariableSource(name, { type: 'value', value: '' });
      return;
    }
    if (type === 'field') {
      setVariableSource(name, { type: 'field', field: Array.from(presentNames)[0] || '' });
      return;
    }
    // An AI Generator binding opens on the setting whose name matches the
    // variable when there is one, so the obvious case is already picked.
    const key: AiConfigKey = (AI_CONFIG_KEYS as readonly string[]).includes(name)
      ? (name as AiConfigKey)
      : 'tone';
    setVariableSource(name, { type: 'ai', field: aiFieldNames[0] || '', key });
  };

  const renderVariable = (variable: WorkflowTemplateVariable) => {
    const source = current.variableSources?.[variable.name];
    const sourceType = source?.type || SOURCE_DEFAULT;
    const defaultNote =
      variable.default !== undefined
        ? variable.default === ''
          ? 'No default of its own'
          : `Default: ${variable.default}`
        : '';

    return (
      <Box
        key={variable.name}
        sx={{ py: 1.25, borderBottom: `1px solid ${ADMIN_COLORS.divider}` }}
      >
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <Typography variant="body2" fontWeight={600}>
            {variable.label}
          </Typography>
          <Typography component="span" variant="caption" color="text.secondary">
            <code>{variable.name}</code>
            {defaultNote && <> — {defaultNote}</>}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
          <TextField
            select
            size="small"
            label="Value from"
            value={sourceType}
            onChange={(e) => setVariableSourceType(variable.name, e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value={SOURCE_DEFAULT}>Template default</MenuItem>
            <MenuItem value="value">Fixed value</MenuItem>
            <MenuItem value="field" disabled={presentNames.size === 0}>
              Form field
            </MenuItem>
            <MenuItem value="ai" disabled={aiFieldNames.length === 0}>
              AI Generator setting
            </MenuItem>
          </TextField>

          {source?.type === 'value' && (
            <TextField
              size="small"
              label="Value"
              value={source.value}
              onChange={(e) =>
                setVariableSource(variable.name, { type: 'value', value: e.target.value })
              }
              sx={{ minWidth: 220 }}
            />
          )}

          {source?.type === 'field' && (
            <TextField
              select
              size="small"
              label="Form field"
              value={source.field}
              onChange={(e) =>
                setVariableSource(variable.name, { type: 'field', field: e.target.value })
              }
              sx={{ minWidth: 220 }}
            >
              {Array.from(presentNames).map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
          )}

          {source?.type === 'ai' && (
            <>
              <TextField
                select
                size="small"
                label="AI Generator field"
                value={source.field}
                onChange={(e) =>
                  setVariableSource(variable.name, { ...source, field: e.target.value })
                }
                sx={{ minWidth: 200 }}
              >
                {aiFieldNames.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Setting"
                value={source.key}
                onChange={(e) =>
                  setVariableSource(variable.name, {
                    ...source,
                    key: e.target.value as AiConfigKey,
                  })
                }
                sx={{ minWidth: 170 }}
              >
                {AI_CONFIG_KEYS.map((key) => (
                  <MenuItem key={key} value={key}>
                    {AI_CONFIG_LABELS[key]}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}
        </Box>

        {source && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            This run uses {sourceLabel(source)}; anything empty falls back to the template default.
          </Typography>
        )}
      </Box>
    );
  };

  const renderField = (field: WorkflowTemplateField) => {
    const present = presentNames.has(field.name);
    const note = fieldNote(field);
    return (
      <Box
        key={field.name}
        sx={{ display: 'flex', gap: 1.5, py: 1, borderBottom: `1px solid ${ADMIN_COLORS.divider}`, alignItems: 'flex-start' }}
      >
        <Typography
          component="span"
          sx={{ color: present ? '#047857' : field.required ? '#d32f2f' : ADMIN_COLORS.textFaint, fontWeight: 700, width: 16 }}
          title={present ? 'Provided by this form' : 'Not in this form'}
        >
          {present ? '✓' : field.required ? '!' : '–'}
        </Typography>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600}>
            <code>{field.name}</code>
            {field.required && <span style={{ color: '#d32f2f' }}> *</span>}
            <Typography component="span" variant="body2" color="text.secondary">
              {' '}
              — {field.label}
            </Typography>
          </Typography>
          {note && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {note}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ maxWidth: 720 }}>
      {submitEveryStep && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This form submits every step (chosen when it was created), so a workflow cannot be
          created from it: it would run once per step.
        </Alert>
      )}

      <FormControlLabel
        control={
          <Switch
            checked={current.enabled}
            disabled={submitEveryStep}
            onChange={(e) => onChange({ ...current, enabled: e.target.checked })}
          />
        }
        label="Create a workflow when this form is submitted"
      />

      {current.enabled && !submitEveryStep && (
        <Box sx={{ mt: 2 }}>
          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Loading templates…</Typography>
            </Box>
          )}
          {error && (
            <Alert severity="error" action={<Button onClick={onReload}>Retry</Button>} sx={{ mb: 2 }}>
              Could not load workflow templates.
            </Alert>
          )}

          {templates && (
            <>
              <TextField
                select
                fullWidth
                size="small"
                label="Template"
                value={current.templateId}
                onChange={(e) => onChange({ ...current, templateId: e.target.value })}
                helperText={template?.description}
                sx={{ mb: 2.5 }}
              >
                {templates.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>

              {template && (
                <>
                  <Alert severity="info" sx={{ mb: 1.5 }}>
                    The visitor's answers fill the workflow. Name your form fields (or add Custom
                    fields with a default value or URL option) exactly as below; a ✓ means it is
                    already provided.
                  </Alert>
                  {template.fields.map(renderField)}
                  {renderField(WORKFLOW_NAME_FIELD)}

                  {variables.length > 0 && (
                    <Box sx={{ mt: 2.5 }}>
                      <Typography variant="subtitle2" sx={{ mb: 0.25 }}>
                        Variable values{' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                          (optional)
                        </Typography>
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 1 }}
                      >
                        Each of the template's variables can be mapped to one of your form's
                        values — a fixed value, another field's answer, or an AI Generator
                        setting. Unmapped variables whose name matches an AI Generator setting
                        respondents are allowed to change pick up their edits automatically;
                        anything else keeps the template's own default.
                      </Typography>
                      {variables.map(renderVariable)}
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default WorkflowSettingsTab;
